/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';

import { css } from '@emotion/css';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { PADDINGS, COLORS2_AS_BACKGROUND } from '^/constants/commonStyles';
import { ReservationDetailed } from '^/types/ReservationDetailed/RawReservationDetailed';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';
import { parseFloatNumberOrNumberString } from '^/util/zod/numberFromString';

import { HoverToReveal } from '../../../../commonComponents/ui/HoverToReveal';
import { AdditionalFees2 } from '../../AdditionalFees2';
import { PODOnsitePaymentHoverMessage } from '../../onsiteInfoMessages';

import {
  billDivider,
  billLabel,
  billRow,
  billSubValue,
  billTitle,
  billValue,
  flexRightmostItemRow,
} from './ReservationDetailDesktop.commonStuff';
import { formatDateString } from '^/util/dateFormat';
import { ExtraChargeDisplayCurrency } from '../../extraChargeDisplay';

const wonCurrency = 'KRW';

function isCancelledAndPODReservation(data: ReservationDetailed) {
  if (data.res_status !== 'canceled' || data.pay_type !== 'POD') {
    throw new Error('DummyErrorForTypeFiltering');
  }
  return data;
}

export const ReservationDetailsBillPODCancelled: React.FC<{
  data: ReturnType<typeof isCancelledAndPODReservation>;
  extraChargeDisplayCurrency?: ExtraChargeDisplayCurrency;
}> = observer(function ReservationDetailsBillPODCancelled({
  data,
  extraChargeDisplayCurrency = 'foreign',
}) {
  const { t } = useTranslation();
  // const tax = data.tax && JSON.parse(data.tax);
  const currency = data.pay_type === 'POD' ? data.currency : data.extra_equipments_json.currency;
  const rate_foreigner_amount = parseFloatNumberOrNumberString(data.rate_foreigner_amount);
  const isKrwDisplay = extraChargeDisplayCurrency === 'krw';
  const isForeignCurrency = currency !== wonCurrency;
  const formatForeignAmount = (value?: number | string | null) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const cancelTime = formatDateString(data.date_cancel, 'YYYY.MM.DD HH:mm (z)', 'Asia/Seoul');

  return (
    <div className="w-full">
      <h3 className={billTitle}>취소 내역</h3>

      <div
        id="separator1"
        className={[
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
          billDivider,
        ].join(' ')}
      />
      <div className="flex flex-col gap-[10px]">
        <div className={billRow}>
          <p className={billLabel}>{t('fee')}</p>
          <p className={billValue}>
            {isForeignCurrency
              ? formatForeignAmount(rate_foreigner_amount)
              : formatKRWWonAmount(rate_foreigner_amount)}
            &nbsp;
            {data.paymentDetails.onSiteCurrency}
          </p>
        </div>
        {data.extra_charge && (data.extra_charge.additional_amount || 0) > 0 && (
          <div className={['mt-[5px]'].join(' ')}>
            <AdditionalFees2
              extra_charge={data.extra_charge}
              onSiteCurrency={data.extra_equipments_json.currency}
              displayCurrency={extraChargeDisplayCurrency}
            />
          </div>
        )}

        <div
          id="separator2"
          className={[
            css`
              margin-top: 12px;
              margin-bottom: 12px;
            `,
            billDivider,
          ].join(' ')}
        />
        {!isKrwDisplay && (
          <>
            <div
              className={[
                billRow,
                css`
                  position: relative;
                `,
              ].join(' ')}
            >
              <div
                className={[
                  billLabel,
                  css`
                    display: flex;
                    align-items: center;
                    justify-items: center;
                    height: fit-content;
                    gap: 6px;
                  `,
                ].join(' ')}
              >
                <span>현장 결제 예상금액</span>
                <HoverToReveal
                  toReveal={
                    <div
                      className={[
                        billSubValue,
                        PADDINGS.TOP_AND_BOTTOM.spacing16,
                        COLORS2_AS_BACKGROUND.sub.MrtGray3,
                        css`
                          position: absolute;
                          left: 0;
                          right: 0;
                          top: 70%;
                          padding-left: 10px;
                          padding-right: 10px;
                          border-radius: 10px;
                          text-align: left;
                        `,
                      ].join(' ')}
                    >
                      {PODOnsitePaymentHoverMessage}
                    </div>
                  }
                />
              </div>
              <div>
                <p className={billValue}>
                  {isForeignCurrency
                    ? formatForeignAmount(data?.total_foreigner_amount)
                    : formatKRWWonAmount(data?.total_foreigner_amount)}
                  &nbsp;
                  {currency === wonCurrency ? t('won') : currency}
                </p>
              </div>
            </div>
            <p className={[billSubValue, flexRightmostItemRow].join(' ')}>
              약 {formatKRWWonAmount(data?.total_amount)} {t('won')}
            </p>
          </>
        )}
      </div>
      <div
        id="separator3"
        className={[
          billDivider,
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
        ].join(' ')}
      />
      <div className={billRow}>
        <p className={billLabel}>취소 일시</p>
        <p className={billValue}>{cancelTime || '-'}</p>
      </div>
    </div>
  );
});
